<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <FancyServerTable
      v-if="isStateLoaded"
      ref="fancyTableRef"
      :data="customerBankAccountsTableData"
      :columns="customerBankAccountsTableHeaders"
      :enable-filters="true"
      :enable-pagination="true"
      :enable-column-visibility="true"
      :enable-column-pinning="true"
      :initial-column-pinning="
        currentSavedState?.columnPinning || {
          left: [],
          right: ['actions'],
        }
      "
      :initial-column-visibility="currentSavedState?.columnVisibility || {}"
      :initial-column-order="currentSavedState?.columnOrder || []"
      :initial-column-sizing="currentSavedState?.columnSizing || {}"
      :initial-column-filters="currentSavedState?.columnFilters || []"
      :initial-sorting="currentSavedState?.sorting ?? []"
      :total-rows="totalRows"
      :page-index="pageIndex"
      :page-size="pageSize"
      :auto-filter="true"
      enable-row-selection
      :get-row-id="row => String(row.id)"
      @update:row-selection="handleRowSelectionChange"
      :loading="isLoading"
      @update:state="debouncedHandleTableStateChange"
      @fetch-data="fetchCustomerBankAccounts"
    >
      <template #actions>
        <CustomerBankAccountsTableBulkActions
          :selectedBankAccounts="selectedCustomerBankAccounts"
          :customerId="customerId"
          @reloadTable="getCustomerBankAccountsTableList"
          @clearSelection="handleClearSelection"
        />
      </template>
      <template #config-manager>
        <FancyTableConfigManager
          :saved-configs="savedTableConfigs"
          :current-config="currentSavedState"
          :is-loading="false"
          :can-save-state="canSaveState"
          @save-config="handleSaveConfig"
          @update-config="handleUpdateConfig"
          @apply-config="handleApplyConfig"
          @delete-config="handleDeleteConfig"
          @mark-as-default="handleMarkAsDefault"
        />
      </template>
    </FancyServerTable>
  </div>
</template>

<script setup lang="ts">
import { h, onMounted, ref } from 'vue';
import type { BankAccountInList } from '@/_helpers/interfaces/bank-accounts/bank-account-in-list.interface';
import { useI18n } from 'vue-i18n';
import { useBankAccountStore } from '@/stores/bank-account.store';
import {
  FancyServerTable,
  type FancyTableColumnDef,
  type ServerTableOptions,
  useTableRenderers,
  FieldType,
  FilterType,
  FancyTableConfigManager,
} from '@/components/shared/fancyTables';
import { printFormat } from 'iban-ts';

import CustomerBankAccountsTableRowActions from './CustomerBankAccountsTableRowActions.vue';
import CustomerBankAccountsTableBulkActions from './CustomerBankAccountsTableBulkActions.vue';
import { useFancyTableConfig } from '@/_helpers/composables/fancy-table-config.composable';

const fancyTableRef = ref<any>(null);
const selectedCustomerBankAccounts = ref<BankAccountInList[]>([]);
// Cache to store all selected row data across pages
const selectedRowsCache = ref<Map<string, any>>(new Map());
const { GetFieldRenderer } = useTableRenderers();

const { getBankAccountsList } = useBankAccountStore();
const { t } = useI18n();

const props = defineProps<{
  customerId: number;
}>();

// Map filters to API params
const mapFiltersToApiParams = (filters: any[]) => {
  const params: any = {};

  filters.forEach(filter => {
    const { id, value } = filter;

    switch (id) {
      case 'bank_name':
        if (value) params.bank_name = value;
        break;
      case 'swift_code':
        if (value) params.swift_code = value;
        break;
      case 'owner_name':
        if (value) params.owner_name = value;
        break;
      case 'iban':
        if (value) params.iban = value;
        break;
      case 'created_on':
        if (value?.from) params.created_after = value.from;
        if (value?.to) params.created_before = value.to;
        break;
      default:
        break;
    }
  });

  return params;
};

// Map sorting to API param
const mapSortingToApiParam = (sorting: any[]): string => {
  if (sorting.length === 0) return '';

  const sort = sorting[0];
  const { id, desc } = sort;
  const backendField = id; // All fields match directly for customer bank accounts

  return desc ? `-${backendField}` : backendField;
};

// Server-side fetch function
const fetchCustomerBankAccounts = async (options?: ServerTableOptions) => {
  isLoading.value = true;

  try {
    const currentPageIndex = options?.pageIndex ?? 0;
    const currentPageSize = options?.pageSize ?? 10;
    const filters = options?.filters ?? [];
    const sorting = options?.sorting ?? [];

    // Update pagination state
    pageIndex.value = currentPageIndex;
    pageSize.value = currentPageSize;

    // Map filters to API params
    const filterParams = mapFiltersToApiParams(filters);

    // Map sorting to API param
    const orderingParam = mapSortingToApiParam(sorting);

    const apiParams = {
      page: currentPageIndex + 1,
      page_size: currentPageSize,
      customer_accounts_only: true,
      customer: props.customerId,
      ...filterParams,
      ...(orderingParam && { ordering: orderingParam }),
    };

    const response = await getBankAccountsList(apiParams);

    customerBankAccountsTableData.value = response.results;
    totalRows.value = response.count;
  } catch (error) {
    console.error('Error fetching customer bank accounts:', error);
    customerBankAccountsTableData.value = [];
    totalRows.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const getCustomerBankAccountsTableList = async () => {
  // This is now a wrapper for fetchCustomerBankAccounts for backward compatibility
  await fetchCustomerBankAccounts();
};

// Initialize the table config composable
const {
  savedTableConfigs,
  currentSavedState,
  canSaveState,
  debouncedHandleTableStateChange,
  handleSaveConfig,
  handleApplyConfig,
  handleUpdateConfig,
  handleDeleteConfig,
  handleMarkAsDefault,
  isStateLoaded,
} = useFancyTableConfig({
  tableName: 'customer-bank-accounts',
  fancyTableRef,
  tableLoader: getCustomerBankAccountsTableList,
});

const isLoading = ref(false);
const customerBankAccountsTableData = ref<BankAccountInList[]>([]);
const totalRows = ref(0);
const pageIndex = ref(0);
const pageSize = ref(10);

const customerBankAccountsTableHeaders: FancyTableColumnDef<BankAccountInList>[] = [
  {
    header: t('suppliers.titles.bank_name'),
    accessorKey: 'bank_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 200,
  },
  {
    header: t('suppliers.titles.swift_placeholder'),
    accessorKey: 'swift_code',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 200,
  },
  {
    header: t('suppliers.titles.account_holder'),
    accessorKey: 'owner_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 250,
  },
  {
    header: t('suppliers.titles.iban'),
    accessorKey: 'iban',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    filterFn: 'iban',
    cell: (info: any) => {
      return printFormat(info.getValue() as string);
    },
    size: 300,
  },
  {
    header: t('suppliers.titles.created_at'),
    enableSorting: true,
    accessorKey: 'created_on',
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 200,
  },
  {
    header: t('common.actions.actions'),
    accessorKey: 'actions',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
    hideFromColumnConfig: true,
    cell: info => {
      return h(CustomerBankAccountsTableRowActions, {
        bankAccount: info.row.original,
        onReloadTable: getCustomerBankAccountsTableList,
      });
    },
    size: 120,
  },
];

const handleClearSelection = () => {
  fancyTableRef.value.resetRowSelection();
  selectedRowsCache.value.clear();
  selectedCustomerBankAccounts.value = [];
};

const handleRowSelectionChange = (selection: any[]) => {
  // Update cache: add newly selected rows from current page
  selection.forEach(row => {
    selectedRowsCache.value.set(String(row.id), row);
  });

  // Remove deselected rows: find rows in current page that are no longer selected
  const currentPageIds = new Set(customerBankAccountsTableData.value.map(row => String(row.id)));
  const selectedIds = new Set(selection.map(row => String(row.id)));

  currentPageIds.forEach(id => {
    if (!selectedIds.has(id)) {
      selectedRowsCache.value.delete(id);
    }
  });

  // Update selectedCustomerBankAccounts with all cached rows
  selectedCustomerBankAccounts.value = Array.from(selectedRowsCache.value.values());
};

onMounted(async () => {
  await getCustomerBankAccountsTableList();
});
</script>
